import React from "react";
import { useGetDashboardQuery } from "../services/api";
import { 
  FiDollarSign, 
  FiShoppingBag, 
  FiAlertTriangle, 
  FiTrendingUp,
  FiPackage,
  FiClock,
  FiCalendar,
  FiArrowUp
} from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB", minimumFractionDigits: 2 }).format(value || 0);
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-ET", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Skeleton Loading Component
function SkeletonCard() {
  return (
    <div className="stat-card" style={{ animation: "pulse 1.5s ease-in-out infinite" }}>
      <div style={{ 
        height: "16px", 
        width: "60%", 
        background: "var(--color-surface-elevated)", 
        borderRadius: "4px",
        marginBottom: "var(--space-3)"
      }}></div>
      <div style={{ 
        height: "36px", 
        width: "40%", 
        background: "var(--color-surface-elevated)", 
        borderRadius: "4px",
        marginBottom: "var(--space-2)"
      }}></div>
      <div style={{ 
        height: "12px", 
        width: "50%", 
        background: "var(--color-surface-elevated)", 
        borderRadius: "4px"
      }}></div>
    </div>
  );
}

export default function DashboardPage() {
  const { data, isLoading, error } = useGetDashboardQuery();
  const dashboard = data?.data;

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "180px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
          <div style={{ 
            height: "20px", 
            width: "300px", 
            background: "#E2E8F0", 
            borderRadius: "4px",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem",
          marginBottom: "2rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "4rem 2rem",
          textAlign: "center"
        }}>
          <FiAlertTriangle size={48} style={{ color: "#EF4444", marginBottom: "1rem" }} />
          <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "0.5rem" }}>
            Unable to load dashboard
          </h3>
          <p style={{ color: "#64748B", marginBottom: "1.5rem" }}>
            Please check your connection and try again.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: "0.65rem 1.5rem",
              background: "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.9rem",
              fontWeight: 500,
              cursor: "pointer"
            }}
          >
            Reload Dashboard
          </button>
        </div>
      </div>
    );
  }

  const kpis = dashboard.kpis || {};
  const lowStock = dashboard.low_stock_items || [];
  const recentSales = dashboard.recent_sales || [];
  const topProducts = dashboard.top_products || [];

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ 
          fontSize: "2rem", 
          marginBottom: "0.5rem", 
          fontWeight: 600,
          color: "#1E293B"
        }}>
          Good morning, Samri!
        </h1>
        <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
          Here's what's happening with your business today.
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Today's Revenue */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💰
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#10B981",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.25rem"
            }}>
              ↑ 12%
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Today's Revenue
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(kpis.today_revenue)}
          </div>
        </div>

        {/* Month Revenue */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              📈
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#64748B",
              fontWeight: 600
            }}>
              {kpis.total_sales} sales
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Month Revenue
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(kpis.month_revenue)}
          </div>
        </div>

        {/* Month Profit */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💵
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#10B981",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.25rem"
            }}>
              ↑ 8%
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Month Profit
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(kpis.month_profit)}
          </div>
        </div>

        {/* Stock Alerts */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: kpis.low_stock_alerts > 0 
                ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
                : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              {kpis.low_stock_alerts > 0 ? "⚠️" : "📦"}
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: kpis.low_stock_alerts > 0 ? "#EF4444" : "#10B981",
              fontWeight: 600
            }}>
              {kpis.low_stock_alerts > 0 ? "Attention" : "Healthy"}
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Stock Alerts
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {kpis.low_stock_alerts}
          </div>
        </div>
      </div>

      {/* Data Panels - Recent Activity & Top Products */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))", gap: "1.25rem", marginBottom: "2rem" }}>
        {/* Recent Sales */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            marginBottom: "1.25rem"
          }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", margin: 0 }}>
              Recent Activity
            </h3>
            <button style={{
              padding: "0.5rem 1rem",
              background: "transparent",
              border: "1px solid #E2E8F0",
              borderRadius: "6px",
              fontSize: "0.85rem",
              color: "#3B82F6",
              cursor: "pointer",
              fontWeight: 500
            }}>
              View all →
            </button>
          </div>
          
          {recentSales.length === 0 ? (
            <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
              <FiShoppingBag size={40} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
              <p style={{ margin: 0, color: "#64748B", fontSize: "0.9rem" }}>No sales yet today</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {recentSales.map((sale) => (
                <div key={sale.id} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",
                  background: "#F8FAFC",
                  borderRadius: "8px",
                  border: "1px solid #F1F5F9",
                  transition: "all 0.2s",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#3B82F6";
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#F1F5F9";
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                      color: "white"
                    }}>
                      🛍️
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: "#1E293B", fontSize: "0.9rem", marginBottom: "0.25rem" }}>
                        {sale.sale_number}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
                        {sale.customer_name || "Walk-in"} • {formatDate(sale.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: "#1E293B", fontSize: "0.95rem", marginBottom: "0.25rem" }}>
                      {formatCurrency(sale.total_amount)}
                    </div>
                    <div style={{
                      fontSize: "0.7rem",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      background: sale.payment_method === "cash" ? "#D1FAE5" : "#DBEAFE",
                      color: sale.payment_method === "cash" ? "#065F46" : "#1E40AF",
                      fontWeight: 600,
                      display: "inline-block"
                    }}>
                      {sale.payment_method === "cash" ? "Cash" : "Bank"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Products */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            marginBottom: "1.25rem"
          }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", margin: 0 }}>
              Top Products
            </h3>
            <span style={{
              padding: "0.35rem 0.75rem",
              background: "#D1FAE5",
              color: "#065F46",
              borderRadius: "6px",
              fontSize: "0.75rem",
              fontWeight: 600
            }}>
              {topProducts.length} items
            </span>
          </div>
          
          {topProducts.length === 0 ? (
            <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
              <FiPackage size={40} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
              <p style={{ margin: 0, color: "#64748B", fontSize: "0.9rem" }}>No product data available</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {topProducts.map((product, idx) => (
                <div key={idx} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1rem",
                  background: "#F8FAFC",
                  borderRadius: "8px",
                  border: "1px solid #F1F5F9",
                  transition: "all 0.2s",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#10B981";
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#F1F5F9";
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1 }}>
                    <div style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      flexShrink: 0
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "#1E293B", fontSize: "0.9rem", marginBottom: "0.25rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {product.product_name}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
                        {product.units_sold} units sold
                      </div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: "#1E293B", fontSize: "0.95rem", flexShrink: 0 }}>
                    {formatCurrency(product.revenue)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Low Stock Alerts */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem"
      }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          marginBottom: "1.25rem"
        }}>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FiAlertTriangle style={{ color: lowStock.length > 0 ? "#EF4444" : "#10B981" }} />
            Low Stock Alerts
          </h3>
          <span style={{
            padding: "0.35rem 0.75rem",
            background: lowStock.length > 0 ? "#FEE2E2" : "#D1FAE5",
            color: lowStock.length > 0 ? "#DC2626" : "#065F46",
            borderRadius: "6px",
            fontSize: "0.75rem",
            fontWeight: 600
          }}>
            {lowStock.length} items
          </span>
        </div>
        
        {lowStock.length === 0 ? (
          <div style={{ 
            padding: "2rem", 
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: "8px",
            textAlign: "center"
          }}>
            <div style={{ 
              width: "56px",
              height: "56px",
              margin: "0 auto 0.75rem",
              borderRadius: "50%",
              background: "#D1FAE5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#10B981",
              fontSize: "1.75rem"
            }}>
              ✓
            </div>
            <h4 style={{ color: "#065F46", marginBottom: "0.5rem", fontSize: "1rem", fontWeight: 600 }}>
              All Stock Levels Healthy
            </h4>
            <p style={{ color: "#64748B", margin: 0, fontSize: "0.9rem" }}>
              No items are currently below minimum stock levels
            </p>
          </div>
        ) : (
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
            gap: "0.75rem" 
          }}>
            {lowStock.map((item) => (
              <div key={item.id} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1rem",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: "8px",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#EF4444";
                e.currentTarget.style.background = "#FEE2E2";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#FECACA";
                e.currentTarget.style.background = "#FEF2F2";
              }}
              >
                <div>
                  <div style={{ fontWeight: 600, marginBottom: "0.25rem", color: "#1E293B", fontSize: "0.9rem" }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
                    {item.material_type}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: "#EF4444", fontSize: "0.95rem", marginBottom: "0.25rem" }}>
                    {Number(item.current_quantity).toFixed(2)} {item.unit}
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "#9CA3AF" }}>
                    Min: {Number(item.min_level).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
